import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { AlertTriangle, Trash2, Loader2 } from 'lucide-react'
import { apiClient } from '../../lib/api'
import { useAuth } from '../../contexts/AuthContext'

interface DeleteAccountDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type Step = 'warning' | 'confirm'

export function DeleteAccountDialog({ open, onOpenChange }: DeleteAccountDialogProps) {
  const { t } = useTranslation('common')
  const { logout } = useAuth()
  const navigate = useNavigate()
  
  const [step, setStep] = useState<Step>('warning')
  const [confirmText, setConfirmText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleClose = () => {
    setStep('warning')
    setConfirmText('')
    setError(null)
    onOpenChange(false)
  }

  const handleContinue = () => {
    setStep('confirm')
  }

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      setError(t('profile.deleteAccount.confirmationMismatch'))
      return
    }

    setIsDeleting(true)
    setError(null)

    try {
      await apiClient.deleteAccount('DELETE')
      logout()
      navigate('/')
    } catch (err) {
      console.error('Failed to delete account:', err)
      setError(t('profile.deleteAccount.deleteFailed'))
      setIsDeleting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="delete-account-dialog sm:max-w-[480px]">
        {step === 'warning' ? (
          <>
            <DialogHeader>
              <div className="delete-warning-icon mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-8 w-8 text-destructive" />
              </div>
              <DialogTitle className="text-center text-xl">
                {t('profile.deleteAccount.warningTitle')}
              </DialogTitle>
              <DialogDescription className="text-center">
                {t('profile.deleteAccount.warningDescription')}
              </DialogDescription>
            </DialogHeader>

            <div className="delete-consequences space-y-3 py-4">
              <p className="text-sm font-medium text-foreground">
                {t('profile.deleteAccount.consequencesTitle')}
              </p>
              <ul className="delete-consequences-list space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-destructive">•</span>
                  {t('profile.deleteAccount.consequence1')}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive">•</span>
                  {t('profile.deleteAccount.consequence2')}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive">•</span>
                  {t('profile.deleteAccount.consequence3')}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive">•</span>
                  {t('profile.deleteAccount.consequence4')}
                </li>
              </ul>
            </div>

            <DialogFooter className="delete-warning-footer flex-col gap-2 sm:flex-row">
              <Button
                variant="outline"
                onClick={handleClose}
                className="w-full sm:w-auto"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleContinue}
                className="w-full sm:w-auto"
              >
                {t('profile.deleteAccount.continueToDelete')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="delete-confirm-icon mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                <Trash2 className="h-8 w-8 text-destructive" />
              </div>
              <DialogTitle className="text-center text-xl">
                {t('profile.deleteAccount.confirmTitle')}
              </DialogTitle>
              <DialogDescription className="text-center">
                {t('profile.deleteAccount.confirmDescription')}
              </DialogDescription>
            </DialogHeader>

            <div className="delete-confirm-input space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  {t('profile.deleteAccount.typeToConfirm')}
                </label>
                <Input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                  placeholder="DELETE"
                  className="text-center font-mono tracking-widest"
                  disabled={isDeleting}
                  autoComplete="off"
                />
              </div>
              
              {error && (
                <p className="delete-error text-center text-sm text-destructive">
                  {error}
                </p>
              )}
            </div>

            <DialogFooter className="delete-confirm-footer flex-col gap-2 sm:flex-row">
              <Button
                variant="outline"
                onClick={() => setStep('warning')}
                disabled={isDeleting}
                className="w-full sm:w-auto"
              >
                {t('common.back')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={confirmText !== 'DELETE' || isDeleting}
                className="w-full sm:w-auto"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('profile.deleteAccount.deleting')}
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t('profile.deleteAccount.deleteForever')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

